/* mv6-header.js — the site-wide header's behavior layer (chrome extraction, 14 Aug 2026).
 *
 * BEHAVIOR PARITY IS THE CONTRACT (CLAUDE.md, user call): everywhere this section
 * renders — PDP, homepage, policy pages, the science article — the header behaves
 * exactly like v14's: hide-on-scroll (hide after 12px of downward travel past 120px,
 * show on 14px up, direct DOM paint, frozen while any overlay holds a scroll lock),
 * burger menu + dropdown with scrim, and the cart-count badge. Since v6.50 it also
 * BROADCASTS the hide state (`mv6:nav`, detail.hidden) — the PDP's sticky offer bar
 * shows only while the header is hidden, so one chrome bar is on screen at a time.
 *
 * ONE mechanism per behavior (R-36): mv6-calm-rest.js owned all of this while the
 * chrome lived inside the PDP section; its copies are ROOT-scoped, so with the
 * chrome outside that root they are inert no-ops — THIS file is the only owner now.
 *
 * The cart button: on a page with a drawer ([data-mv6-cart-panel] anywhere in the
 * document) it dispatches `mv6:cart-open`, which mv6-calm-rest.js listens for.
 * On a page without one it navigates to the theme's /cart.
 */
(function () {
  'use strict';

  var ROOT = document.getElementById('mv6-header');
  if (!ROOT) return;
  var CART_URL = ROOT.getAttribute('data-mv6-cart-url') || '/cart';

  function $(s) { return ROOT.querySelector(s); }
  function $$(s) { return Array.prototype.slice.call(ROOT.querySelectorAll(s)); }

  /* ── menu ─────────────────────────────────────────────────────────────── */
  var menuOpen = false;

  /* ── THE PAGE BEHIND THE MENU MUST NOT SCROLL (19 Aug 2026, user report) ──
     Diagnosed, not guessed: this menu has never taken a scroll lock. menu()
     writes ONE state attribute and nothing else, and the two overlays that DO
     lock (the cart drawer's `mv6-locked`, the facts sheet's `mv6-facts-locked`)
     each own their own class — the menu was simply the third overlay that was
     never given one. The chrome extraction is what made it visible: the menu's
     scrim went from an absolute strip inside the PDP root to a full-viewport
     fixed layer on every template, so a dropdown floating over moving content
     now reads as a bug at both widths.

     ⚠ NOT the drawer's `html{overflow:hidden}`. At desktop width that rule
     re-resolves the PDP's sticky gallery column and reads as "the left half of
     the page disappeared" (R-26 / R-50), which is exactly why the facts sheet's
     overflow lock is mobile-only. ⚠ NOT the body pin either (R-52): a pinned
     body makes `window.scrollY` read ~0, and THIS file's hide-on-scroll reads
     that value — R-80 is the header flashing for a second when the facts sheet
     closed. The menu needs neither mechanism, because it has no scroller of its
     own: refusing the gesture touches no layout, moves no scroll offset, and so
     cannot break sticky, the pin, hide-on-scroll or another overlay's lock.
     R-52's warning that an event guard "cannot help" was about the SHEET, whose
     own scroller had to be let through and chained to the document once it hit
     an end; there is no such exemption here, so there is no leak path.

     Attached only WHILE open. A permanent non-passive touchmove listener on
     window would drag every touch scroll on every page onto the main thread —
     this header is site-wide, and scroll cost in the chrome is what R-74/R-75
     were written about. */
  function stopScroll(e) {
    if (e.touches && e.touches.length > 1) return;   /* pinch-zoom stays available */
    e.preventDefault();
  }
  var SCROLL_KEYS = { ' ': 1, Spacebar: 1, PageUp: 1, PageDown: 1, Home: 1, End: 1, ArrowUp: 1, ArrowDown: 1 };
  function stopScrollKeys(e) {
    if (!SCROLL_KEYS[e.key] || e.metaKey || e.ctrlKey || e.altKey) return;
    /* Never swallow a key a control owns: Space activates the burger and the
       dropdown's links, arrows move a caret inside a field. */
    if (e.target && e.target.closest && e.target.closest('a,button,input,select,textarea,[contenteditable="true"]')) return;
    e.preventDefault();
  }
  /* ?mv6t=nolock — ships the menu WITHOUT its scroll lock, so the lock can be
     A/B'd from the URL in one build instead of a deploy (R-111: one variable per
     reading). The flag is read here rather than in the registry below because the
     registry runs after this closure is built. */
  var LOCK_OFF = /[?&#]mv6t=(?:[a-z0-9,]*,)?(?:nolock|all)(?:,|$)/i.test(location.search + location.hash);
  var scrollBlocked = false;
  function blockScroll(on) {
    if (LOCK_OFF) return;
    if (on === scrollBlocked) return;
    scrollBlocked = on;
    var m = on ? 'addEventListener' : 'removeEventListener';
    window[m]('wheel', stopScroll, { passive: false });
    window[m]('touchmove', stopScroll, { passive: false });
    window[m]('keydown', stopScrollKeys);
  }

  function menu(open) {
    menuOpen = open;
    /* ONE attribute — the !important CSS in mv6-header.liquid owns both
       endpoints (R-39/R-73). The first version wrote inline styles here, and
       at the top of the page the open transition FROZE one frame in (live
       diagnostic: dropdown at scale .9615, opacity 0 — five rounds had read
       that as a clip or a stacking trap). Never touch the nav itself on
       open/close: it carries backdrop-filter, and writing to it re-rasterises
       the blurred layer (R-32). Ladder: scrim 50, nav 60 (R-40). */
    if (open) ROOT.setAttribute('data-mv6-menu-open', '');
    else ROOT.removeAttribute('data-mv6-menu-open');
    blockScroll(open);
  }

  ROOT.addEventListener('click', function (e) {
    var el = e.target.closest ? e.target.closest('[data-mv6-menu-toggle],[data-mv6-menu-scrim],[data-mv6-cart-open],[data-mv6-jump],[data-mv6-menu-link]') : null;
    if (!el) return;
    if (el.hasAttribute('data-mv6-menu-toggle')) { menu(!menuOpen); return; }
    if (el.hasAttribute('data-mv6-menu-scrim')) { menu(false); return; }
    if (el.hasAttribute('data-mv6-menu-link')) { menu(false); return; }   /* dropdown links: close, then navigate */
    if (el.hasAttribute('data-mv6-cart-open')) {
      if (document.querySelector('[data-mv6-cart-panel]')) {
        menu(false);
        try { document.dispatchEvent(new CustomEvent('mv6:cart-open')); } catch (err) {}
      } else {
        window.location.href = CART_URL;
      }
      return;
    }
    if (el.hasAttribute('data-mv6-jump')) {
      /* Land on the element the link NAMES (14 Aug 2026, user call): homepage
         CTAs point at #mv6-buy so the visitor arrives at the product images,
         PDP links keep #mv6-title. Fallback stays the title. */
      var hash = el.getAttribute('href') || '';
      /* Fallback = the target this TEMPLATE names (index → #mv6-buy, the start
         of the product images — user call, 14 Aug 2026 round 3; else #mv6-title),
         so the header's Shop-now lands like the page's own CTAs. */
      var target = (hash.charAt(0) === '#' && document.getElementById(hash.slice(1)))
        || document.getElementById(ROOT.getAttribute('data-mv6-jump-target') || 'mv6-title')
        || document.getElementById('mv6-title');
      if (!target) { menu(false); return; }
      e.preventDefault();
      var nav = $('[data-mv6-nav]');
      /* Flush landing for any target other than #mv6-title — see the note in
         mv6-calm-rest.js's jump handler (14 Aug 2026 round 2). */
      var pad = target.id === 'mv6-title' ? (nav ? nav.getBoundingClientRect().height : 0) + 12 : -1;
      var y = target.getBoundingClientRect().top + (window.scrollY || 0) - pad;
      window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      menu(false);
    }
  });

  document.addEventListener('keydown', function (e) {
    /* Ungated on purpose: menu(false) is idempotent, and gating it on the flag
       let flag and DOM desync under fast toggling (same lesson as the binder). */
    if (e.key === 'Escape') menu(false);
  });

  /* ── hide-on-scroll — v14's ACCUMULATOR (hide at +12 past 120px, show at −14
     of accumulated up-travel, reset on direction change), on a JS-PINNED nav.
     ⚠ WHY PINNING EXISTS (14 Aug 2026, round 3 — the real cause of "the header
     never comes back"): the nav's inline position:sticky worked in v14 because
     the nav lived inside the full-page section root — its containing block was
     page-tall. In the extracted header SECTION the containing block is the
     ~120px group wrapper: sticky has no room, the nav scrolls away like any
     static element, and painting transforms on it changes nothing (which is
     why the round-2 accumulator fix changed nothing live — R-70). The nav is
     switched to position:fixed once the page scrolls past its natural spot
     (a same-height placeholder holds the layout) and unfixed back into flow
     near the top so the announcement bar stacks above it again. */
  var drawerOpen = false;
  document.addEventListener('mv6:drawer', function (e) {
    drawerOpen = !!(e.detail && e.detail.open);
    if (drawerOpen) menu(false);
  });

  function locked() {
    var h = document.documentElement.classList;
    return drawerOpen || h.contains('mv6-locked') || h.contains('mv6-facts-locked');
  }

  /* ── hide-on-scroll — v14's ACCUMULATOR, ported verbatim (14 Aug 2026 live
     round). The first version compared per-FRAME deltas (y vs lastY ±12/14),
     and a slow scroll-up never moves 14px in one frame — the header never
     came back. v14 accumulates same-direction travel and resets on direction
     change: hide at +12 past 120px, show at −14, always visible in the top
     120px. FROZEN while any overlay holds a scroll lock — lastY re-syncs and
     acc resets every frozen frame, so a lock release never reads as travel. */
  var navEl = $('[data-mv6-nav]');
  var ph = null, pinTop = 0, navFixed = false;
  if (navEl) {
    ph = document.createElement('div');
    /* No inline display here — the stylesheet derives the placeholder's
       visibility from data-mv6-nav-pinned on #mv6-header (R-132). */
    ph.setAttribute('aria-hidden', 'true');
    ph.setAttribute('data-mv6-nav-ph', '');   /* named so ?mv6t=nopin can drop it */
    navEl.parentNode.insertBefore(ph, navEl);
  }

  function measurePin() {
    if (!navEl || locked()) return;   /* a body pin makes geometry bogus (R-52) */
    /* R-132 addendum 2 (23 Aug 2026, caught on the v6.48.1 draft): this used to
       early-return while PINNED — but on phones the user has often scrolled
       (= pinned) before load/fonts arrive, so every re-measure was skipped and
       a pre-font pinTop froze forever. Announcement bar taller after fonts →
       pin engaged while the bar was still on screen: white gap below it (the
       states were CONSISTENT — the watcher's 0 violations — the geometry was
       wrong). Pinned now measures the PLACEHOLDER, which occupies the nav's
       flow slot: no unpin, no flash. */
    if (navFixed) {
      if (!ph) return;
      var pr = ph.getBoundingClientRect();
      pinTop = Math.max(1, pr.top + (window.scrollY || 0));
      var nh = navEl.getBoundingClientRect().height;
      if (nh > 10) ph.style.height = nh + 'px';
      return;
    }
    var r = navEl.getBoundingClientRect();
    /* Clamped positive (Clarity round, 22 Aug 2026): a pinTop of 0 or less pins the
       nav AT the top of the page — fixed over the announcement bar, placeholder gap
       in the flow. Unpinned at scrollY 0 is an invariant, not a measurement. */
    pinTop = Math.max(1, r.top + (window.scrollY || 0));
    ph.style.height = r.height + 'px';
  }

  /* R-132 (23 Aug 2026, Clarity round 2) · ONE attribute owns the pin. This
     used to write four things per flip — placeholder display, nav position,
     top/left/right — and the 23 Aug Clarity sessions kept catching the
     in-between frames: placeholder visible over a nav in flow ("white gap
     above the header"), stale hidden-state on an unpinned nav ("no header at
     all"). The v6.46 self-heal repaired the gap one scroll frame later; this
     removes the frame instead. The stylesheet in mv6-header.liquid derives
     BOTH the placeholder's display and the nav's fixed position from
     data-mv6-nav-pinned on #mv6-header, so the states cannot desync — the
     menu's R-39/R-73 pattern. Round 7's restore-never-remove lesson retires
     with the inline writes: the stylesheet owns resting position:relative
     permanently, so z-index:60 can never die with a deleted declaration. */
  function pinSync(y) {
    var want = y > pinTop;
    if (want === navFixed) return;
    navFixed = want;
    if (want) ROOT.setAttribute('data-mv6-nav-pinned', '');
    else {
      ROOT.removeAttribute('data-mv6-nav-pinned');
      navPaint(false);
      navHidden = false; acc = 0;
    }
  }

  var lastY = null, acc = 0, navHidden = false, wasLocked = false;
  var hideTimer = 0;

  /* R-75 (round 8): a HIDDEN fixed nav must stop existing for the compositor.
     translateY(-102%) leaves a live viewport-fixed layer (translucent white +
     backdrop-filter + will-change) parked just off-screen; on iOS that layer
     composites at stale offsets under load — full-width nav-height light bands
     over the footer while scrolling (one user frame shows the Shop-now pill
     ghost INSIDE the band), and transient flashes beside the buy box during
     tap-driven relayouts that read as "the card is shaking". Same lesson as
     the cart drawer's closed state (visibility:hidden delayed past the slide).
     Show restores visibility BEFORE the transform so the slide-in is visible.

     Round 12 (15 Aug 2026): the SHOWN state was still promoted. This function
     used to park `transform:translateY(0)` inline on show, and the markup
     shipped the same declaration plus `will-change:transform` — so the visible
     nav was a live compositor layer on the scroll path through every buy-box
     tap (R-74: chrome at rest ships ZERO transforms). Rounds 8-11 dropped every
     layer EXCEPT this one, which is why the header on/off A/B kept fingering a
     section whose known actors were all gone. Show now CLEARS the transform. */
  function navPaint(hidden) {
    /* v6.50 (4 Sep 2026, user call): the PDP's sticky offer bar follows THIS
       state — bar shows while the header is hidden (reader scrolling down),
       bar retracts when the header returns (reader scrolling up). One owner
       of scroll direction (R-36): the header decides, the bar listens. The
       event carries the value, so a late listener never has to infer it. */
    try { document.dispatchEvent(new CustomEvent('mv6:nav', { detail: { hidden: !!hidden } })); } catch (e) {}
    if (hidden) {
      navEl.style.transform = 'translateY(-102%)';
      clearTimeout(hideTimer);
      /* Attribute + stylesheet own the hidden state (R-39/R-73) — round 8
         wrote style.visibility inline, which this theme has beaten before. */
      hideTimer = setTimeout(function () { if (navHidden) navEl.setAttribute('data-mv6-nav-hidden', ''); }, 320);
    } else {
      clearTimeout(hideTimer);
      navEl.removeAttribute('data-mv6-nav-hidden');
      navEl.style.visibility = '';
      navEl.style.transform = '';
    }
  }

  function navSync() {
    if (!navEl) return;
    /* ROUND 19 — the header flash when the Supplement Facts sheet closes.
       A body-pinned lock (R-52) parks `body` at `position:fixed; top:-Y`, so
       while the sheet is open `window.scrollY` reads ~0. The old freeze branch
       ran pinSync FIRST and then re-synced `lastY = 0` on every frozen frame,
       so: (a) the pin was re-evaluated against a bogus 0 and unpinned the nav
       mid-page, and (b) on close the restore to the real offset arrived as a
       ~3000px scroll-DOWN — the nav slid away right as the sheet faded, which
       is the "header appears for a second, then disappears" report.
       Frozen now means FROZEN: no pin change, no lastY write, no accumulation.
       The first unlocked frame re-syncs lastY to the restored position and
       swallows that one delta, so opening and closing an overlay leaves the
       header in exactly the state it had before. */
    if (locked()) { acc = 0; wasLocked = true; return; }
    /* Clamped at BOTH ends (22 Aug 2026, live report): the top clamp was always
       here; the bottom one wasn't. iOS's bottom rubber-band overshoots past the
       scroll max and settles back — that settle is a negative delta ≥14, which
       read as "scrolling up" and un-hid the header AT the footer during a
       scroll-down. Clamping to the document's real range makes the bounce
       invisible to the accumulator. */
    var yMax = Math.max(0, (document.documentElement.scrollHeight || 0) - window.innerHeight);
    var y = Math.min(Math.max(0, window.scrollY || 0), yMax);
    if (wasLocked) { wasLocked = false; lastY = y; acc = 0; pinSync(y); return; }
    pinSync(y);
    var lastKnown = lastY == null ? y : lastY;
    var delta = y - lastKnown;
    lastY = y;
    if (y < 120) {
      acc = 0;
      if (navHidden) { navHidden = false; navPaint(false); }
      return;
    }
    acc = (delta > 0) === (acc > 0) ? acc + delta : delta;
    if (acc > 12 && !navHidden) { navHidden = true; acc = 0; navPaint(true); }
    else if (acc < -14 && navHidden) { navHidden = false; acc = 0; navPaint(false); }
  }

  /* ⚠ iOS Safari fires resize CONTINUOUSLY while the URL bar collapses/expands
     during scroll. The first version unpinned + force-measured on every one of
     those — a layout thrash that janked the whole page ("micro freezes", text
     shimmer) and painted transient gaps under the nav. Only a WIDTH change can
     move pinTop (the announcement bar's clamp paddings), so height-only
     resizes are ignored entirely. */
  var lastW = window.innerWidth;
  window.addEventListener('resize', function () {
    if (window.innerWidth === lastW) return;
    lastW = window.innerWidth;
    if (navEl && navFixed) pinSync(0);   /* unpin to measure the natural state */
    measurePin();
    navSync();
  }, { passive: true });
  if (document.readyState !== 'complete') window.addEventListener('load', function () { measurePin(); navSync(); });
  measurePin();
  navSync();
  /* Fonts and settles move the announcement bar's height (R-89 class) — the
     pinned-safe measurePin above makes these re-measures free. */
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { measurePin(); navSync(); });
  [400, 1400, 2600].forEach(function (ms) { setTimeout(function () { measurePin(); }, ms); });

  /* R-128 · bfcache: a back-forward restore resumes whatever chrome state the page
     left with, against a scroll position the browser restores on its own. Reset:
     menu shut, nav re-measured from a clean unpinned state. */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    menu(false);
    if (navEl) { if (navFixed) pinSync(0); measurePin(); lastY = null; acc = 0; navSync(); }
  });

  /* ?mv6t=pinhud — DIAGNOSTIC ONLY (never a fix, never left on): a live HUD
     for phones, where there is no console. Shows y / pinTop / state / the
     announcement bar's bottom edge, and counts state-mismatch violations.
     Screenshot it the moment a white gap is visible. */
  if (/[?&]mv6t=[^&#]*pinhud/.test(location.search) && navEl) {
    var hud = document.createElement('div');
    hud.style.cssText = 'position:fixed;left:8px;bottom:8px;z-index:2147483647;background:rgba(20,16,28,.88);color:#9FE8B8;font:11px/1.5 ui-monospace,monospace;padding:8px 10px;border-radius:10px;pointer-events:none;white-space:pre';
    document.body.appendChild(hud);
    var hudViol = 0;
    (function hudTick() {
      var pin = ROOT.hasAttribute('data-mv6-nav-pinned');
      var pos = getComputedStyle(navEl).position;
      var phD = ph ? getComputedStyle(ph).display : '-';
      var ann = ROOT.children[0];
      var annB = (ann && ann !== ph && ann !== navEl) ? Math.round(ann.getBoundingClientRect().bottom) : -999;
      if ((pin && pos !== 'fixed') || (!pin && phD !== 'none')) hudViol++;
      hud.textContent = 'y ' + Math.round(window.scrollY || 0) + '  pinTop ' + Math.round(pinTop) +
        '\npinned ' + pin + '  pos ' + pos + '  ph ' + phD +
        '\nannBottom ' + annB + '  viol ' + hudViol;
      requestAnimationFrame(hudTick);
    })();
  }

  var ticking = false;
  window.addEventListener('scroll', function () {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () { try { navSync(); } finally { ticking = false; } });   /* one thrown frame must not wedge every future sync */
  }, { passive: true });

  /* ── cart-count badge ─────────────────────────────────────────────────────
     On a money page, window.mv6 is the single source of truth (its /cart.js
     refresh and every mutation flow through state()); this section's script tag
     runs BEFORE the deferred purchase module (section groups render first), so
     bind by polling briefly. On a page without the module, one /cart.js read. */
  function paint(n) {
    var b = $('[data-mv6-cart-count]');
    if (!b) return;
    n = parseInt(n, 10) || 0;
    /* Round 11: DIFF-GUARDED. This ran unguarded on EVERY mv6 state emit —
       every plan/tile tap wrote textContent + toggled [hidden] inside the
       FIXED nav, dirtying the header's subtree at exactly tap time. With the
       header section removed the shake stopped (user A/B, run WITH rounds
       6–8 published) — this was one of the two header actors left. */
    var txt = n > 0 ? String(n) : '';
    if (b.textContent !== txt) b.textContent = txt;
    var hid = b.hasAttribute('hidden');
    if (n > 0 && hid) b.removeAttribute('hidden');
    else if (n <= 0 && !hid) b.setAttribute('hidden', '');
  }

  var tries = 0;
  function bindBadge() {
    if (window.mv6 && window.mv6.on && window.mv6.state) {
      window.mv6.on('change', function (s) { if (s && s.totals) paint(s.totals.itemCount); });
      var s0 = window.mv6.state();
      if (s0 && s0.totals) paint(s0.totals.itemCount);
      return;
    }
    if (++tries < 6) { setTimeout(bindBadge, 350); return; }
    fetch(CART_URL + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (c) { paint(c.item_count); })
      .catch(function () {});
  }
  bindBadge();

  /* ── ?mv6t=<flag[,flag]> — one-tap A/B switches (round 12) ─────────────
     The shake needs one variable per measurement, but each republish costs the
     user a deploy. These flags let ONE build test three more suspects from the
     URL. Inert without the flag — nothing below runs on a normal page load.
       ?mv6t=note      the ot-single note never mounts (kills its display flip;
                       no empty band, unlike round 5's visibility reservation)
       ?mv6t=hover     no :hover transform on plan cards / bottle tiles — iOS
                       makes :hover STICK to the tapped element, so a tap lifts
                       the card 3px and leaves it lifted
       ?mv6t=marquee   the trust strip below the buy box stops animating and
                       drops its mask + will-change layer
       ?mv6t=all       every switch below and above
     A shake that survives all three, on a build where the nav no longer rests
     promoted, means the cause is in the buy box's own relayout — not a layer.

     ROUND 13 (15 Aug 2026) — note/hover/marquee all came back NEGATIVE on a
     de-promoted nav, in both browsers, so the cause is inside the buy box's
     own paint. These three cut the only animated/promoted things left in it:
       ?mv6t=lift      the SELECTED sub card's resting transform:translateY(-3px)
                       (mv6-styles ~931) — a permanent compositor layer in the
                       DEFAULT state, immediately above the card that shakes
       ?mv6t=ring      the selected card's box-shadow (44px-blur drop + a
                       0 0 0 3px spread ring) animating over 280ms per flip
       ?mv6t=notrans   NO transitions or animations anywhere in the offer
                       controls — the master discriminator: clean here means an
                       animated property is the shake, still shaking means a
                       synchronous relayout/paint and the next step is a
                       USB-tethered paint-flash profile
       ?mv6t=flat      lift + ring + notrans together

     ROUND 16 (15 Aug 2026) — the Chrome-iOS pair (footer band + nav detach):
       ?mv6t=oldcanvas restores the OLD root background, the viewport-sized
                       `linear-gradient(180deg,#FFF 50%,#2A2435 50%)` on html.
                       If the band comes back with this on and stays away with
                       it off, the band was the gradient's hard stop sliding
                       with Chrome's URL-bar viewport resize.
       ?mv6t=vvpin     compensates the pinned nav with visualViewport.offsetTop
                       so it tracks the VISUAL viewport instead of the layout
                       one. Candidate fix for "content shows above the nav, then
                       it snaps" in Chrome iOS. Read R-71 before shipping it by
                       default: a handler that forces layout on iOS's per-frame
                       viewport events janked the entire site once. This one only
                       writes a transform and never measures.
       ?mv6t=nobar     (round 18) removes the sticky offer bar entirely. The bar
                       is fixed to the bottom, full width, translucent white with
                       backdrop-filter and a 34px shadow, and parked off-screen
                       by a transform for most of a visit — R-75's defect in the
                       PDP section, which is why the band survived every header
                       and footer switch. Round 18 drops its layer with
                       visibility; this switch is the fallback discriminator.
       ?mv6t=nooverlays removes the cart panel, cart scrim and facts wrapper.

     ROUND 25 (15 Aug 2026):
       ?mv6t=nopending disables the cart-action busy feedback (R-83). The flag
                       is read by mv6-pending.js itself, which returns before
                       binding; the registry entry exists so the badge reports
                       it as KNOWN and to neutralise a cached older script.

     ⚠ ROUND 14 (15 Aug 2026) — EVERY SWITCH IS NOW SPECIFICITY-PROOF. The
     round-13 build's `hover`, `lift`, `ring` and `notrans` were all defeated by
     id-scoped !important rules (mv6-styles 926-931 for the selection
     transform/shadow, 1173-1174 for hover, 1046-1048 for the transition-property
     clamp — a LONGHAND, which also beats a shorthand `transition:none`). Only
     `note` and `marquee` ever applied, so only those two negatives count. Now:
     stylesheet switches carry a DOUBLE-ID prefix (#root#root, specificity (2,x,y),
     which no author rule here can reach) and set longhands explicitly; `lift`,
     `ring` and `price` are written inline with setProperty(…,'important'),
     which no author rule can beat at all. R-77.
       ?mv6t=price     NEW — the hero price row (the wrapping flex row holding
                       heroPrice/heroWas/heroPct/heroSave) is hidden outright.
                       Tests the nocompare hypothesis: those spans are only
                       visibility:hidden, so they still take part in flex sizing,
                       and render() rewrites their TEXT on every flip — a width
                       change can rewrap the row and move every card below it.
                       data-mv6-nocompare is 0 at sub@1, 1 at ot@1, 0 at ot@2-3,
                       which fits five of the six rows in the shake table.
       ?mv6t=all       every switch

     Sanity check before trusting ANY reading: each switch must be visibly
     different. lift ⇒ the selected Subscribe card sits 3px lower. ring ⇒ no
     glow on either selected card. price ⇒ the big price line above the cards is
     gone. notrans ⇒ selection colour snaps with no fade. If you cannot see it,
     it did not apply and the reading is worthless.

     ⚠ ROUND 17 (15 Aug 2026) — THE BADGE IS THE SWITCH'S OWN SELF-TEST, and it
     is now unconditional. Round 16's `oldcanvas` and `vvpin` were DEAD: the
     early `if (!css && !inlineTest) return;` fired before either block, because
     neither contributes stylesheet CSS or card-inline CSS — so the user's A/B
     measured nothing, and the only reason we know is that they noticed the
     little TEST callout never appeared. R-77 recurring in a third shape (first
     specificity, then longhand-vs-shorthand, now control flow). The block below
     is a REGISTRY: every flag is a function in one table, the badge paints before
     any of them run, and an unrecognised flag names itself on screen. A flag
     cannot be silently dead any more. */
  (function () {
    var m = /[?&#]mv6t=([a-z0-9,]+)/i.exec(location.search + location.hash);
    if (!m) return;
    var on = m[1].toLowerCase().split(','), all = on.indexOf('all') >= 0;
    var flat = on.indexOf('flat') >= 0;
    function has(f) { return all || on.indexOf(f) >= 0; }

    var ROOTEL = document.querySelector('[data-mv6-mode]');
    var RID = ROOTEL && ROOTEL.id ? ROOTEL.id : '';
    /* Double id — specificity (2,n,m). Every author rule in mv6-styles tops out
       at ONE id, so this wins regardless of source order or !important. */
    var P = RID ? '#' + RID + '#' + RID + ' ' : '';
    function sel(list) { return list.map(function (s) { return P + s; }).join(','); }
    var css = '';
    function rule(r) { css += r; }
    function sheet(text, tag) {
      var s = document.createElement('style');
      s.setAttribute('data-mv6-test', tag);
      s.textContent = text;
      document.head.appendChild(s);
    }
    /* Inline !important beats every author rule regardless of specificity, and is
       re-applied for 5s because a re-render must never restore what a test removes. */
    var inlineJobs = [];
    function inline(fn) { inlineJobs.push(fn); }

    /* THE REGISTRY. Add a flag here or it does not exist — there is no other
       code path, so a flag can never be dead (round 17). */
    var FLAGS = {
      note: function () { rule(sel(['[data-mv6-when="ot-single"]']) + '{display:none !important}'); },
      hover: function () { rule(sel(['[data-mv6-pick]:hover', '[data-mv6-tile]:hover']) + '{transform:none !important}'); },
      marquee: function () { rule(sel(['[data-mv6-marquee]', '[data-mv6-ticker]']) + '{animation:none !important;will-change:auto !important;-webkit-mask:none !important;mask:none !important}'); },
      /* transition-property explicitly: the round-2 clamp is a longhand !important,
         and a shorthand `transition:none` does not beat it. */
      notrans: function () { rule(sel(['[data-mv6-card]', '[data-mv6-card] *', '[data-mv6-tile]', '[data-mv6-tile] *', '[data-mv6-when]', '[data-mv6-when] *', '[data-mv6-pick]', '[data-mv6-cta]']) + '{transition:none !important;transition-property:none !important;transition-duration:0s !important;animation:none !important}'); },
      lift: function () { inline(function (cards) { for (var i = 0; i < cards.length; i++) cards[i].style.setProperty('transform', 'none', 'important'); }); },
      ring: function () { inline(function (cards) { for (var i = 0; i < cards.length; i++) cards[i].style.setProperty('box-shadow', '0 8px 18px -12px rgba(74,58,110,.28)', 'important'); }); },
      price: function () {
        inline(function () {
          var hp = document.querySelector('[data-mv6="heroPrice"]');
          if (hp && hp.parentElement) hp.parentElement.style.setProperty('display', 'none', 'important');
        });
      },
      /* ── footer / canvas family ── */
      oldcanvas: function () { sheet('html{background:linear-gradient(180deg,#FFFFFF 50%,#2A2435 50%) !important}', 'oldcanvas'); },
      nowave: function () { sheet('[data-mv6-ft-wave]{display:none !important}', 'nowave'); },
      nomark: function () { sheet('[data-mv6-ft-wordmark]{visibility:hidden !important}', 'nomark'); },
      plainfoot: function () { FLAGS.nowave(); FLAGS.nomark(); },
      /* Round 18: the PDP section's own parked layers. nobar is the fallback
         discriminator for the band if R-75's visibility drop is not enough — it
         removes the sticky offer bar from the page entirely. */
      nobar: function () { sheet('[data-mv6-bar]{display:none !important}', 'nobar'); },
      /* ══ v6.34 — THE BAND IS NOT IN OUR MARKUP ══════════════════════════
         All four parked layers this theme owns (cart panel, cart scrim, facts
         wrapper and, since v6.34, the offer bar) are display:none from first
         paint, and the user re-tested on a phone: the band SURVIVED, still
         PDP-only. That is a clean elimination at last — R-111's condition met,
         no second actor live — so every layer in our own markup is exonerated.

         v6.34 ships the permanent half of the answer in mv6-styles: Judge.me
         and Seal inject widget containers on PRODUCT pages, v6 renders none of
         them, so those containers are now hidden. These three switches are the
         other half — they tell you what to do if the band is STILL there.

           ?mv6t=noapps  (= no3p, all-letters name — PREFER THIS ONE)
           ?mv6t=no3p    every third-party injected root, including all app
                         embeds. Shopify renders app embeds into
                         [id^="shopify-block-"]; OUR sections are
                         "shopify-section", so this can never touch the theme.
                         Band gone ⇒ it is an app we have not caught yet.
                         Band still there ⇒ it is not in the DOM at all, and the
                         remaining explanation is a compositing bug against the
                         footer's stacking context — which #mv6-footer-shell's
                         isolate (v6.34) already guards.
           ?mv6t=nojdgm  Judge.me only — run if no3p is positive.
           ?mv6t=noasw   Sienna only — run if no3p is positive, nojdgm is not.

         DIAGNOSTICS, never fixes. no3p removes the accessibility widget and the
         consent tooling; shipping any of them as a default is a compliance
         problem. They are inert without the URL flag. */
      /* ⚠ v6.35 — USE ?mv6t=noapps. It is the same switch under an all-letters
         name. v6.34 shipped no3p and it came back UNKNOWN on the user's phone
         because the RUNTIME parser was /mv6t=([a-z,]+)/i — letters only — so
         "no3p" truncated to "no" before the registry was ever consulted. That is
         the same defect check-paint.js had, in the same round, and it means the
         gate was accidentally RIGHT: "no" really was being dispatched and really
         was dead. Both are [a-z0-9] now, and the alias below means the diagnostic
         works even if some other reader of the query string is still letter-only.
         Prefer noapps in every instruction you give a human. */
      noapps: function () { FLAGS.no3p(); },
      no3p: function () { FLAGS.nojdgm(); FLAGS.noasw(); sheet('[id^="shopify-block-"],.klaviyo-form,[class*="kl-private"],[id^="sealsubs"],[class^="sealsubs"]{display:none !important}', 'no3p'); },
      nojdgm: function () { sheet('[class^="jdgm"],[class*=" jdgm"],[id^="jdgm"],.jdgm-widget,.jdgm-rev-widg{display:none !important}', 'nojdgm'); },
      noasw: function () { sheet('#asw-widget,.asw-widget,.asw-container,.asw-menu,.asw-menu-btn,[class^="asw-"]{display:none !important}', 'noasw'); },
      nooverlays: function () { sheet('[data-mv6-cart-scrim],[data-mv6-cart-panel],[data-mv6-facts-wrap]{display:none !important}', 'nooverlays'); },
      /* Round 33 (15 Aug 2026) — THE BAND IS BACK, on iOS SAFARI this time, over
         the footer's statements block (user screenshot). Every parked layer this
         theme owns is already dropped: the sticky bar and both overlays take
         display:none when shut, the hidden nav takes visibility:hidden, the shown
         nav carries no transform and no blur, and round 16 removed the viewport-
         positioned canvas gradient that explained the Chrome-only version. What is
         left is the nav in its PINNED state — and a pinned header cannot exist
         without a viewport-fixed compositor layer, so source alone cannot answer
         it. This switch unpins the nav (it scrolls away with the page, placeholder
         dropped) so ONE pass down the footer answers the question a rules file
         cannot: band with the nav pinned, no band without it.
         DIAGNOSTIC, not a candidate fix — shipping it would delete hide-on-scroll. */
      nopin: function () { sheet('#mv6-header nav[data-mv6-nav]{position:relative !important;top:auto !important;left:auto !important;right:auto !important}#mv6-header [data-mv6-nav-ph]{display:none !important}', 'nopin'); },
      /* Round 25: registered so the badge stops calling it UNKNOWN. Unlike the
         others, the WORK of this switch happens in mv6-pending.js, which reads
         the flag itself and returns before it binds anything — by the time this
         registry runs, no [data-mv6-pending] will ever be written. The CSS here
         is only a belt for a cached copy of the old script; the switch is not
         dead, and the registry is still the single source of truth for what
         exists (round 17's contract). */
      /* R-124 · restores the old `overflow-x:clip; overflow-y:visible` pair that
         desktop Safari promoted to a scroll container (trackpad could not scroll
         the PDP or homepage). Written INLINE with !important because the author
         rule it re-creates carries an id inside :has() and no stylesheet switch
         can out-specify it (R-77). Diagnostic only — it re-breaks Safari. */
      oldclip: function () {
        inline(function () {
          var els = [document.body, document.querySelector('main'), document.getElementById('MainContent'),
                     document.querySelector('.shopify-section:has([data-mv6-mode])')];
          els.forEach(function (e) {
            if (!e) return;
            e.style.setProperty('overflow-x', 'clip', 'important');
            e.style.setProperty('overflow-y', 'visible', 'important');
          });
        });
      },
      /* Registered so the badge reports it as KNOWN — the work happens above, in
         blockScroll, which reads the flag itself (same shape as nopending). */
      nolock: function () {},
      /* Registered so the badge reports it as KNOWN — the pin HUD is read by its
         own regex near the pageshow block (it needs pinTop, which lives in that
         closure; this registry runs outside it — the nolock shape). Caught by
         check-paint downstream on the v6.48.2 deploy and registered there first;
         mirrored back per R-45/R-87. */
      pinhud: function () {},
      /* R-126 · forces overscroll-behavior back to auto on html/body. Kept as a
         one-tap check because this property cost four rounds: in desktop Safari
         `overscroll-behavior: none` on the document killed the wheel gesture while
         keyboard, scrollbar and programmatic scrolling all kept working. If any
         future report says "the trackpad does nothing but the arrows work", try
         this switch before anything else. */
      noos: function () {
        inline(function () {
          [document.documentElement, document.body].forEach(function (e) {
            if (!e) return;
            e.style.setProperty('overscroll-behavior', 'auto', 'important');
            e.style.setProperty('overscroll-behavior-x', 'auto', 'important');
            e.style.setProperty('overscroll-behavior-y', 'auto', 'important');
          });
        });
      },
      /* ── ?mv6t=scrollprobe — WHY WON'T THIS PAGE SCROLL, answered ON SCREEN ──
         Desktop Safari cannot scroll the PDP or the homepage; other templates in
         the same Safari are fine, Chrome is fine, iOS is fine. Two fixes have now
         gone out on a reasoned mechanism instead of a measured one (R-124 twice),
         and the machine that reproduces it has no console available. So this prints
         the answer as a readout: it is a DIAGNOSTIC, never a fix, inert without the
         flag, and pointer-events:none so it can never block anything.

         The decisive line is SCROLLBY. If the page moves when JS scrolls it but not
         when the trackpad does, the document is scrollable and the INPUT is being
         swallowed (a non-passive wheel listener, or WebKit routing the gesture to a
         fixed layer under the cursor). If it does not move either, the document has
         no scroll range at all and the cause is overflow or height. Everything else
         here exists to name the culprit once that split is known. */
      scrollprobe: function () {
        var run = function () {
          var H = document.documentElement, B = document.body;
          var cs = function (el, p) { return el ? getComputedStyle(el)[p] : '-'; };
          var y0 = window.scrollY || H.scrollTop || 0;
          window.scrollBy(0, 120);
          var moved = (window.scrollY || H.scrollTop || 0) - y0;
          window.scrollTo(0, y0);
          var ev = new WheelEvent('wheel', { bubbles: true, cancelable: true, deltaY: 120 });
          window.dispatchEvent(ev);
          var mid = document.elementFromPoint(Math.round(innerWidth / 2), Math.round(innerHeight / 2));
          var chain = [], n = mid, guard = 0;
          while (n && n.nodeType === 1 && guard++ < 14) {
            var s = getComputedStyle(n);
            if (s.overflowX !== 'visible' || s.overflowY !== 'visible' || s.position === 'fixed' || s.contain !== 'none' || s.transform !== 'none') {
              chain.push((n.tagName.toLowerCase()) + (n.id ? '#' + n.id : '') +
                ' ov:' + s.overflowX + '/' + s.overflowY + ' pos:' + s.position +
                (s.contain !== 'none' ? ' contain:' + s.contain : '') +
                (s.transform !== 'none' ? ' tf' : ''));
            }
            n = n.parentElement;
          }
          var fixedTall = [];
          var all = document.querySelectorAll('div,section,main,aside');
          for (var i = 0; i < all.length; i++) {
            var e = all[i], st = getComputedStyle(e), r = e.getBoundingClientRect();
            if (st.position === 'fixed' && r.width > innerWidth * 0.9 && r.height > innerHeight * 0.6) {
              fixedTall.push((e.tagName.toLowerCase()) + (e.id ? '#' + e.id : '') +
                ' ov:' + st.overflowY + ' pe:' + st.pointerEvents + ' vis:' + st.visibility + ' z:' + st.zIndex);
            }
          }
          var box = document.createElement('div');
          box.style.cssText = 'position:fixed;left:6px;right:6px;bottom:6px;z-index:2147483000;background:rgba(20,14,30,.94);color:#B9F3CE;font:11px/1.4 ui-monospace,Menlo,monospace;padding:9px 11px;border-radius:9px;pointer-events:none;white-space:pre-wrap;max-height:60vh;overflow:hidden';
          box.textContent = [
            'SCROLLBY moved: ' + moved + 'px   (0 = document has no scroll range)',
            'wheel cancelled by a listener: ' + ev.defaultPrevented,
            'scrollingElement: ' + (document.scrollingElement === H ? 'html' : (document.scrollingElement === B ? 'body' : String(document.scrollingElement && document.scrollingElement.tagName))),
            'html ov: ' + cs(H, 'overflowX') + '/' + cs(H, 'overflowY') + '   h:' + cs(H, 'height') + '   classes:' + (H.className || '-'),
            'body ov: ' + cs(B, 'overflowX') + '/' + cs(B, 'overflowY') + '   pos:' + cs(B, 'position') + '   top:' + cs(B, 'top') + '   h:' + cs(B, 'height'),
            'scrollHeight ' + H.scrollHeight + ' vs viewport ' + innerHeight + '   scrollY ' + Math.round(y0),
            'under cursor-centre: ' + (mid ? mid.tagName.toLowerCase() + (mid.id ? '#' + mid.id : '') : '-'),
            'blocking chain: ' + (chain.length ? chain.join(' | ') : 'none'),
            'full-viewport fixed: ' + (fixedTall.length ? fixedTall.join(' | ') : 'none'),
            '\u2192 SCREENSHOT THIS'
          ].join('\n');
          document.body.appendChild(box);
        };
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { setTimeout(run, 400); });
        else setTimeout(run, 400);
      },
      nopending: function () { sheet('[data-mv6-pending]::after{display:none !important}[data-mv6-pending]{opacity:1 !important;cursor:auto !important;pointer-events:auto !important}', 'nopending'); },
      /* Round 30: the hero capsule parallax, back on mobile after R-86 was
         reversed (its removal did not stop the judder it was blamed for). Same
         shape as nopending — mv6-home.js reads the flag itself and never paints;
         the CSS here is the belt that also clears a transform left by a cached
         older script. If the phone ever judders in the hero again, this is the
         one-tap discriminator. */
      nocaps: function () { sheet('[data-mv6-cap-layer],[data-mv6-cap-field]{transform:none !important}', 'nocaps'); },
      vvpin: function () {
        if (!window.visualViewport || !navEl) return;
        var vv = window.visualViewport;
        var vvSync = function () {
          if (!navFixed) { navEl.style.transform = ''; return; }
          /* navPaint owns the transform while the nav is hiding or hidden — it
             parks translateY(-102%) there. Chrome iOS fires visualViewport
             events throughout the URL-bar animation, i.e. exactly while the
             user is scrolling, so without this guard every hide is overwritten
             with translateY(offsetTop) and the nav pops back into view
             mid-slide. ?mv6t=vvpin then gets judged on a header that no longer
             hides — R-77's failure one layer up: the reading breaks, not the
             code.
             ⚠ THIS LINE LIVES HERE NOW. It was re-applied by hand to the repo
             after five consecutive zips because the generator did not carry it
             (15 Aug 2026, R-87). Do not drop it when regenerating. */
          if (navHidden) return;
          navEl.style.transform = 'translateY(' + Math.max(0, vv.offsetTop) + 'px)';
        };
        vv.addEventListener('resize', vvSync);
        vv.addEventListener('scroll', vvSync);
        vvSync();
      }
    };

    /* The badge paints FIRST and unconditionally: it is the switch set's own
       self-test. "No badge" now means the script did not load at all, never
       "the flag quietly did nothing" (round 17). */
    var unknown = on.filter(function (f) { return f !== 'all' && f !== 'flat' && !FLAGS[f]; });
    var tag = document.createElement('div');
    tag.style.cssText = 'position:fixed;left:8px;top:8px;z-index:2147483000;background:' +
      (unknown.length ? '#8A2C2C' : '#4A3A6E') +
      ';color:#fff;font:10px/1.35 ui-monospace,Menlo,monospace;padding:5px 7px;border-radius:6px;pointer-events:none;white-space:pre';
    tag.textContent = unknown.length
      ? 'TEST ' + on.join('+') + '\nUNKNOWN: ' + unknown.join(',')
      : 'TEST ' + on.join('+');
    var showTag = function () { if (document.body) document.body.appendChild(tag); };
    if (document.body) showTag(); else document.addEventListener('DOMContentLoaded', showTag);
    if (unknown.length) console.error('[mv6t] unknown switch(es): ' + unknown.join(',') + ' — nothing was applied for them.');

    Object.keys(FLAGS).forEach(function (f) { if (has(f) || (flat && (f === 'lift' || f === 'ring' || f === 'notrans'))) FLAGS[f](); });

    if (css) sheet(css, on.join(','));
    if (inlineJobs.length) {
      var apply = function () {
        var cards = document.querySelectorAll('[data-mv6-card]');
        inlineJobs.forEach(function (job) { job(cards); });
      };
      apply();
      document.addEventListener('DOMContentLoaded', apply);
      var t = 0, iv = setInterval(function () { apply(); if (++t > 20) clearInterval(iv); }, 250);
    }
  })();

  /* ── ?mv6debug=1 — on-phone trace overlay (round 7) ────────────────────
     The buy-box shake reproduces ONLY on the phone, where there is no console.
     Open any page with ?mv6debug=1 (or #mv6debug): a readout pins to the
     bottom of the screen, records 1s of frame-by-frame geometry after every
     plan/tile tap (ot-card top · CTA top · scrollY · visualViewport offset),
     and says SCREENSHOT THIS. scrollY stepping with the card = scroll
     anchoring; vvTop stepping = the iOS URL bar; both flat = layout/paint.
     pointer-events:none — it can never block a tap. Inert without the flag. */
  if (/mv6debug/.test(location.search + location.hash)) (function () {
    var box = document.createElement('div');
    box.style.cssText = 'position:fixed;left:8px;right:8px;bottom:8px;z-index:2147483000;background:rgba(20,14,30,.93);color:#B9F3CE;font:11px/1.45 ui-monospace,Menlo,monospace;padding:10px 12px;border-radius:10px;pointer-events:none;white-space:pre-wrap;max-height:46vh;overflow:hidden';
    box.textContent = '[mv6debug] armed — tap a plan card or a bottle tile';
    document.body.appendChild(box);
    var R = document.querySelector('[data-mv6-mode]');
    var ot = R && R.querySelector('[data-mv6-card="ot"]'), cta = R && R.querySelector('[data-mv6-cta]');
    /* Round 14: the hero price row's HEIGHT is traced too. Its compare spans are
       only visibility:hidden, so they still size the flex row, and render()
       rewrites their text on every flip — if a width change rewraps the row, its
       height steps and every card below moves. hRow changing while otTop follows
       it = the nocompare/rewrap mechanism, caught in the act. */
    var hp = R && R.querySelector('[data-mv6="heroPrice"]');
    var hrow = hp && hp.parentElement;
    if (!ot || !cta) { box.textContent = '[mv6debug] no buy box on this page'; return; }
    document.addEventListener('click', function (e) {
      var el = e.target.closest && e.target.closest('[data-mv6-pick],[data-mv6-tile]');
      if (!el) return;
      var t0 = performance.now(), rows = [], last = '';
      (function f() {
        var vv = window.visualViewport ? window.visualViewport.offsetTop.toFixed(0) : '-';
        var sig = [ot.getBoundingClientRect().top.toFixed(1), cta.getBoundingClientRect().top.toFixed(1), hrow ? hrow.getBoundingClientRect().height.toFixed(1) : '-', (window.scrollY || 0).toFixed(0), vv].join(' ');
        if (sig !== last) { rows.push(((performance.now() - t0) | 0) + ' ' + sig); last = sig; }
        if (performance.now() - t0 < 1000) requestAnimationFrame(f);
        else box.textContent = 'tap: ' + (el.getAttribute('data-mv6-pick') || 'tile ' + el.getAttribute('data-mv6-tile')) + '   ms otTop ctaTop hRow scrollY vvTop\n' + rows.join('\n') + '\n\u2192 SCREENSHOT THIS and send it';
      })();
    }, true);
  })();
})();


/* ══ v6.39 — THE CART BUTTON ON PAGES THAT HAVE NO DRAWER ═══════════════════
   Reported: on standalone pages (/pages/faq, /pages/90-day-guarantee, the policy
   pages) clicking the cart icon sometimes opened the /cart PAGE instead of the
   drawer. Confirmed by the user: never on the PDP, never on the homepage.

   Cause, and it is structural rather than a race. The HEADER is site-wide — it
   lives in the header group. The DRAWER is not: snippets/mv6-cart-drawer.liquid
   is rendered by sections/mv6-calm-rest.liquid and sections/mv6-home.liquid, and
   its behaviour is bound by mv6-calm-rest.js, which those two sections also load.
   So on every other template the button exists, the panel it targets does not,
   and nothing owns the click — the theme's own cart handling wins it, sometimes.

   ── v6.40 · THE DRAWER IS SITE-WIDE NOW, AND THIS IS THE BELT ──────────────
   The block below was v6.39's holding position: make the wrong destination
   DETERMINISTIC rather than "sometimes". v6.40 does the real thing instead —
   snippets/mv6-cart-chrome.liquid renders the drawer, mv6-purchase.js and
   mv6-calm-rest.js in sections/mv6-footer-global.liquid, which IS on every
   template. So `[data-mv6-cart-panel]` now exists everywhere and the early
   return below fires on every page: nothing here runs in production.

   It stays, unchanged, for the one case it still answers — the footer section
   removed from the footer group, or its "Cart drawer on all other pages"
   checkbox turned off. Then the button has no panel again and a plain,
   predictable navigation is better than a click nobody owns. Delete it only
   when the drawer cannot be turned off. */
;(function () {
  'use strict';
  function wire() {
    if (document.querySelector('[data-mv6-cart-panel]')) return;   /* drawer is here — leave it alone */
    var btns = document.querySelectorAll('[data-mv6-cart-open]');
    for (var i = 0; i < btns.length; i++) {
      (function (b) {
        if (b.getAttribute('data-mv6-cart-href')) return;
        b.setAttribute('data-mv6-cart-href', '');
        b.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          window.location.href = '/cart';
        }, true);   /* capture, so it wins before any theme handler */
      })(btns[i]);
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire);
  else wire();
})();
